import theme from 'src/theme';

export const DATE_FILTER_LIST = [
  { id: '1', name: 'Today' },
  { id: '2', name: '2 Days' },
  { id: '7', name: '7 Days' },
  { id: '30', name: '30 Days' },
];

export const chartInputStyles = {
  mr: 4,
  my: 1,
  fontSize: 16,
  color: '#111827',
  '& .MuiSelect-standard': {
    color: '#111827'
  },
  '& .MuiSelect-icon': {
    color: '#111827'
  },
  '&:before': {
    borderBottomColor: '#9CA3AF'
  },
  '&:after': {
    borderBottomColor: '#111827'
  }
};

export const chartOptions = {
  animation: true,
  responsive: true,
  cornerRadius: 20,
  layout: { padding: 0 },
  legend: { display: false },
  maintainAspectRatio: false,
  tooltips: {
    backgroundColor: theme.palette.background.paper,
    bodyFontColor: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    borderWidth: 1,
    enabled: true,
    footerFontColor: theme.palette.text.secondary,
    intersect: false,
    mode: 'index',
    titleFontColor: theme.palette.text.secondary
  }
};

export const echartOptions = {
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    }
  },
  legend: {},
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },
  xAxis: {
    type: 'value',
    boundaryGap: [0, 0.01]
  },
};

// Vehicle Usage Pie Chart
export const getVehicleUsageChartConfig = (data) => ({
  tooltip: { trigger: 'item', position: [10, 10] },
  legend: { bottom: 0, left: 'center' },
  color: ['#4dd0e1', '#ff7043', '#ffca28', '#2563EB', '#8bc34a'],
  series: [{
    name: 'Fuel Consumption',
    type: 'pie',
    radius: '80%',
    label: { position: 'inside', formatter: '{c}' },
    itemStyle: { borderRadius: 4, borderColor: '#eee', borderWidth: 1 },
    bottom: 50,
    data,
    emphasis: {
      label: { show: true, fontSize: 30, fontWeight: 'bold' },
      itemStyle: {
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowColor: 'rgba(0, 0, 0, 0.5)'
      }
    },
  }]
});

// Vehicle Status Pie Chart
export const getVehicleStatusChartConfig = (data) => ({
  tooltip: { trigger: 'item', position: [10, 10] },
  legend: { bottom: 0, left: 'center' },
  series: [{
    name: 'Vehicle Status',
    type: 'pie',
    radius: ['40%', '90%'],
    label: { position: 'inside', formatter: '{c}' },
    itemStyle: { borderRadius: 4, borderColor: '#eee', borderWidth: 1 },
    bottom: 30,
    color: ['#e53935', '#8bc34a', '#ffca28'],
    data,
    emphasis: {
      label: { show: true, fontSize: 30, fontWeight: 'bold' },
      itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.5)' },
    },
  }]
});

// Sales Line Chart
export const getSalesChartData = (data) => {
  const { datasets, labels } = data;

  const colorPalette = ['#4CAF50', '#2196F3', '#FB8C00', '#ffca28', '#ff7043'];

  const chartDataSet = labels.map((label, i) => [
    label,
    ...datasets.map((ds) => ds.data[i] ?? 0),
  ]);

  const series = datasets.map((dataset, index) => {
    const baseColor = colorPalette[index % colorPalette.length];
    return {
      name: dataset.label || `Series ${index + 1}`,
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 10,
      lineStyle: {
        width: 4,
        color: baseColor,
        shadowColor: baseColor,
        shadowBlur: 2,
      },
      itemStyle: {
        color: baseColor,
        borderColor: '#fff',
        borderWidth: 2,
      },
      areaStyle: {
        opacity: 0.3,
        color: baseColor,
      },
      emphasis: {
        focus: 'series',
        itemStyle: {
          borderColor: baseColor,
          borderWidth: 3,
        },
        lineStyle: {
          width: 5,
        },
      },
    };
  });

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#2c2c2c',
      borderColor: '#444',
      textStyle: { color: '#fff', fontWeight: 'bold', fontSize: 13 },
      axisPointer: {
        type: 'cross',
        label: { backgroundColor: '#2563EB', color: '#fff' },
      },
    },
    legend: {
      type: isMobile ? 'scroll' : 'plain',
      data: datasets.map((d) => d.label || 'Unknown'),
      top: 4,
      left: 'center',
      itemHeight: isMobile ? 10 : 14,
      itemWidth: isMobile ? 18 : 24,
      icon: 'roundRect',
      padding: isMobile ? [5, 10] : [8, 16],
      orient: 'horizontal',
      backgroundColor: '#f5f5f5',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: '#ddd',
      textStyle: {
        fontSize: isMobile ? 10 : 14,
        fontWeight: 600,
        color: '#37474f',
        padding: [0, isMobile ? 3 : 6]
      },
      inactiveColor: '#b0bec5',
      ...(isMobile && {
        width: '90%',
        pageButtonItemGap: 5,
        pageIconSize: 10,
      }),
    },
    grid: {
      left: '5%', right: '10%', bottom: '12%', top: '15%', containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      axisLabel: {
        rotate: 0, fontSize: 13, fontWeight: 600, color: '#455a64', margin: 16
      },
      axisLine: { lineStyle: { color: '#90caf9', width: 1 } },
      axisTick: {
        show: true,
        alignWithLabel: true,
        length: 6,
        lineStyle: { color: '#90caf9', width: 1.5 },
      },
      splitLine: { show: false },
      name: 'Date',
      nameLocation: 'end',
      nameTextStyle: {
        fontSize: 14, fontWeight: 'bold', color: '#0d62bcff', padding: [12, 0, 0, 0]
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: '#4a4a4a',
        padding: [0, 4, 0, 0],
        formatter: (val) => val.toLocaleString(),
      },
      axisLine: { lineStyle: { color: '#8f1010ff', width: 2 } },
      axisTick: { show: true, lineStyle: { color: '#bbb', width: 1 } },
      splitLine: { show: false },
      name: 'Quantity',
      nameTextStyle: {
        fontSize: 14, fontWeight: 'bold', color: '#0d62bcff', padding: [0, 16, 20, 0]
      },
    },
    dataset: {
      source: [['product', ...datasets.map((ds) => ds.label || 'Series')], ...chartDataSet],
    },
    series,
    color: colorPalette,
    animation: true,
    animationDuration: 800,
    animationEasing: 'cubicOut',
  };
};

// Vehicle Utilization Chart
export const getVehicleUtilizationChartData = (data) => {
  const categories = Object.keys(data);
  const chartData = categories.map((category) => data[category]?.length || 0);

  return {
    legend: {},
    tooltip: {
      formatter: (params) => `Vehicle Utilization<br/>${params.marker}${params.name}&nbsp;&nbsp;&nbsp;&nbsp;<strong>${params.value}</strong>`
    },
    color: ['#4dd0e1', '#2563EB', '#ff7043', '#ffca28', '#8bc34a'],
    xAxis: { type: 'category', data: ['0 - 25%', '25 - 50%', '50 - 75%', '75 - 100%', 'Above 100%'] },
    yAxis: { type: 'value' },
    series: [{
      data: chartData,
      type: 'bar',
      barMaxWidth: 34,
      barCategoryGap: '45%'
    }]
  };
};
export const getSalesTimeSeriesData = (data) => {
  const { labels = [], datasets = [] } = data;

  // Color mapping
  const colorMap = {
    Inward: '#2563EB',
    Issued: '#8bc34a',
    Transfer: '#ff7043'
  };

  const series = datasets.map((item) => {
    const seriesName = item?.name || item?.label || 'Unknown';
    return {
      name: seriesName,
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      data: item?.data || [],
      lineStyle: { width: 3 },
      itemStyle: { color: colorMap[seriesName] || '#000' },
      areaStyle: { opacity: 0.1, color: colorMap[seriesName] || '#000' }
    };
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary
        }
      }
    },
    legend: {
      top: 10,
      textStyle: { color: theme.palette.text.primary },
      data: datasets.map((d) => d?.name || d?.label || 'Unknown')
    },
    grid: {
      left: '3%', right: '4%', bottom: '3%', containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: labels,
      axisLine: { lineStyle: { color: theme.palette.divider } },
      axisLabel: { rotate: 45 }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: theme.palette.divider } },
      splitLine: { lineStyle: { color: theme.palette.divider, type: 'dashed' } }
    },
    series,
    color: Object.values(colorMap),
    animation: true
  };
};
